import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaSignInAlt,
  FaSignOutAlt,
  FaTachometerAlt,
  FaBars,
  FaTimes,
  FaBoxOpen,
  FaInfoCircle,
  FaArrowRight,
} from "react-icons/fa";
import { FaPeoplePulling } from "react-icons/fa6";
import AuthContext from "../AuthContext";
import "./Navbar.css";

// In-page anchor links — these only make sense on the home page, so from any
// other page they navigate to "/" first and the target section is scrolled
// into view once HomePage mounts (see the hash-scroll effect in HomePage.js).
const ANCHOR_LINKS = [
  { label: "Features", hash: "#features" },
  { label: "How It Works", hash: "#how-it-works" },
  { label: "Use Cases", hash: "#use-cases" },
  { label: "Pricing", hash: "#pricing" },
];

const Navbar = () => {
  const { isLoggedIn, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
    setMenuOpen(false);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className="bg-gray-900 text-white sticky top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-3">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 text-xl sm:text-2xl font-bold shrink-0"
          onClick={closeMenu}
        >
          <FaPeoplePulling />
          CustomerQueue.com
        </Link>

        {/* Hamburger (mobile) */}
        <button
          className="sm:hidden text-2xl focus:outline-none"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {ANCHOR_LINKS.map((link) => (
            <Link
              key={link.hash}
              to={`/${link.hash}`}
              className="px-3 py-2 rounded text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition"
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <Link
            to="/about"
            className="px-3 py-2 rounded flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition"
          >
            <FaInfoCircle /> About
          </Link>

          <Link
            to="/other-products"
            className="px-3 py-2 rounded flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition"
          >
            <FaBoxOpen /> Other Products
          </Link>

          {isLoggedIn ? (
            <>
              <Link
                to="/dashboard"
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded flex items-center gap-2 transition text-sm font-semibold"
              >
                <FaTachometerAlt /> Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded flex items-center gap-2 transition text-sm font-semibold"
              >
                <FaSignOutAlt /> Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="px-3 py-2 rounded flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition"
              >
                <FaSignInAlt /> Login
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-4 py-2 rounded-lg flex items-center gap-2 transition text-sm font-bold shadow-md shadow-blue-900/30"
              >
                Get Started <FaArrowRight className="text-xs" />
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile Dropdown */}
      {menuOpen && (
        <div className="sm:hidden bg-gray-800 px-4 py-3 space-y-2">
          {ANCHOR_LINKS.map((link) => (
            <Link
              key={link.hash}
              to={`/${link.hash}`}
              onClick={closeMenu}
              className="block bg-gray-700/60 hover:bg-gray-700 px-4 py-2 rounded transition"
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/about"
            onClick={closeMenu}
            className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded flex items-center gap-2 transition"
          >
            <FaInfoCircle /> About
          </Link>

          <Link
            to="/other-products"
            onClick={closeMenu}
            className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded flex items-center gap-2 transition"
          >
            <FaBoxOpen /> Other Products
          </Link>

          {isLoggedIn ? (
            <>
              <Link
                to="/dashboard"
                onClick={closeMenu}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded flex items-center gap-2 transition"
              >
                <FaTachometerAlt /> Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="w-full text-left bg-red-600 hover:bg-red-700 px-4 py-2 rounded flex items-center gap-2 transition"
              >
                <FaSignOutAlt /> Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={closeMenu}
                className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded flex items-center gap-2 transition"
              >
                <FaSignInAlt /> Login
              </Link>
              <Link
                to="/register"
                onClick={closeMenu}
                className="bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-2 rounded flex items-center justify-center gap-2 transition font-bold"
              >
                Get Started <FaArrowRight className="text-xs" />
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
